/**
 * Pricing Card Component
 * 
 * Displays pricing plans in chat.
 */

import React from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';

interface PricingCardProps {
  onSignup: () => void;
}

interface Plan {
  name: string;
  price: string;
  period: string;
  features: string[];
  isPopular?: boolean;
  ctaText: string;
}

const plans: Plan[] = [
  {
    name: 'Starter',
    price: '€49',
    period: '/month',
    features: [
      '1 LinkedIn account',
      '500 contacts/month',
      'AI message writing',
      'Basic analytics',
      'Email support',
    ],
    ctaText: 'Start Free Trial',
  },
  {
    name: 'Professional',
    price: '€99',
    period: '/month',
    features: [
      '3 LinkedIn accounts',
      '2,000 contacts/month',
      'Advanced AI writing',
      'Full analytics',
      'A/B testing',
      'Priority support',
    ],
    isPopular: true,
    ctaText: 'Start Free Trial',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    features: [
      'Unlimited accounts',
      'Unlimited contacts',
      'Custom AI training',
      'API access',
      'Dedicated success manager',
    ],
    ctaText: 'Contact Us',
  },
];

export const PricingCard: React.FC<PricingCardProps> = ({ onSignup }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={classes.card}>
      <Typography variant="h6" className={classes.title}>
        {t('publicChat.pricingTitle', 'Choose Your Plan')}
      </Typography>
      <Typography variant="body2" className={classes.subtitle}>
        {t('publicChat.pricingSubtitle', '14-day free trial on all plans. Cancel anytime.')}
      </Typography>

      <Box className={classes.plans}>
        {plans.map((plan) => (
          <Box
            key={plan.name}
            className={`${classes.plan} ${plan.isPopular ? classes.popularPlan : ''}`}
          >
            {plan.isPopular && (
              <Chip
                icon={<StarIcon sx={{ fontSize: 14 }} />}
                label="Most Popular"
                size="small"
                className={classes.popularBadge}
              />
            )}

            <Typography variant="subtitle1" className={classes.planName}>
              {plan.name}
            </Typography>

            <Box className={classes.priceRow}>
              <Typography variant="h4" className={classes.price}>
                {plan.price}
              </Typography>
              {plan.period && (
                <Typography variant="body2" className={classes.period}>
                  {plan.period}
                </Typography>
              )}
            </Box>

            <Box className={classes.features}>
              {plan.features.map((feature, i) => (
                <Box key={i} className={classes.feature}>
                  <CheckCircleIcon className={classes.checkIcon} />
                  <Typography variant="body2" className={classes.featureText}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Button
              variant={plan.isPopular ? 'contained' : 'outlined'}
              fullWidth
              onClick={onSignup}
              className={plan.isPopular ? classes.primaryButton : classes.secondaryButton}
            >
              {plan.ctaText}
            </Button>
          </Box>
        ))}
      </Box>

      <Typography variant="caption" className={classes.guarantee}>
        💸 30-day money-back guarantee on all plans
      </Typography>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: `1px solid ${theme.palette.greys.lighter}`,
    padding: theme.spacing(3),
    maxWidth: 800,
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  plans: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  plan: {
    backgroundColor: theme.palette.backgrounds.main_new,
    borderRadius: 12,
    border: `1px solid ${theme.palette.greys.lighter}`,
    padding: theme.spacing(2.5),
    position: 'relative',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: theme.palette.primary.light,
      transform: 'translateY(-2px)',
    },
  },
  popularPlan: {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}0A`,
  },
  popularBadge: {
    position: 'absolute',
    top: -10,
    right: 12,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: '0.7rem',
    height: 22,
    '& .MuiChip-icon': {
      color: theme.palette.primary.contrastText,
    },
  },
  planName: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
  },
  price: {
    color: theme.palette.text.primary,
    fontWeight: 700,
  },
  period: {
    color: theme.palette.text.secondary,
  },
  features: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  checkIcon: {
    fontSize: 16,
    color: theme.palette.success.main,
  },
  featureText: {
    color: theme.palette.text.primary,
    fontSize: '0.85rem',
  },
  primaryButton: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    color: theme.palette.primary.contrastText,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 8,
    '&:hover': {
      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
    },
  },
  secondaryButton: {
    borderColor: theme.palette.greys.light,
    color: theme.palette.text.primary,
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: 8,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}0A`,
    },
  },
  guarantee: {
    display: 'block',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

export default PricingCard;

